import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export type Step = {
  /**
   * For legacy reasons, some steps (such as agreements or documents) handle their own progression
   * when there are multiple substeps. We do not want the general `goToPreviousStep`/`goToNextStep`
   * handlers to go through substeps for these. New steps (with substeps) should let these handle
   * transitioning through the substeps, and can omit specifying this property.
   */
  doNotIterateSubsteps?: boolean;
  id?: string | null;
  label?: string;
  route: string;
  /**
   * Some routes (e.g., "invite.show.agreement" and "invite.show.document") map to the same step.
   * In those cases, specify `sameAsRoute` to be the other route(s) that map to this same step.
   */
  sameAsRoute?: string | string[];
  substeps?: Step[];
  /**
   * If this is true, when navigating back into this step, go to the first substep, not the last.
   */
  useFirstSubstepWhenMovingBackwards?: boolean;
};

/**
 * This service is used to maintain internal state when moving forward/backward through steps for an invite.
 * Most of the actual transition logic lives in `app/routes/invites/show.ts`; future work will extract some of that
 * to here to more closely resemble `VisitorStepsService`. For now, all this service does is maintain the state of whether
 * the last move was backward or forward.
 * The service approach is used to avoid exposing query params.
 */
export default class InviteStepsService extends Service {
  @tracked backwards = false;

  @tracked transitionedFromStep: Step | null = null;

  @tracked deniedOnLoad = false;
}
